import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Button, CustomScrollbar, CustomScrollbarRef, Icon } from 'rs-emd-ui-atoms';
import { IPLPCategoryItem } from '../../../models/plp/plp-category-item.model';
import { IPLPService } from '../../../services/plp/plp-service';
import styles from './categories-area-component.module.scss';

type CategoriesAreaProps = {
  categoriesArea: IPLPCategoryItem[];
  plpService: IPLPService;
  getSRPFamilyPageUrl: (familyId: number | string) => string;
};

export const CategoriesArea: FunctionComponent<CategoriesAreaProps> = (props: CategoriesAreaProps) => {
  const [openCategory, setOpenCategory] = useState<IPLPCategoryItem | undefined>(undefined);
  const [categoryJustClosed, setCategoryJustClosed] = useState<IPLPCategoryItem | undefined>(undefined);
  const modalRef = useRef<Array<HTMLDivElement | null>>([]);
  const ps = useRef<Array<CustomScrollbarRef | null>>([]);

  // reset selected facet on clickoutside
  useEffect(() => {
    /**
     * If clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (openCategory) {
        let index = props.categoriesArea?.findIndex((f) => f === openCategory) ?? -1;
        if (
          index > -1 &&
          modalRef &&
          modalRef.current != null &&
          modalRef.current[index] !== event.target &&
          !modalRef.current[index]?.contains(event.target as any)
        ) {
          setCategoryJustClosed(openCategory);
          setOpenCategory(undefined);
        }
      }
    }

    if (openCategory) {
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalRef, openCategory, props.categoriesArea]);

  useEffect(() => {
    // call update scroll to re-calculate scroll length
    let index = props.categoriesArea?.findIndex((f) => f === openCategory) ?? -1;
    if (index > -1) ps.current[index]?.updateScroll();
  }, [openCategory, props.categoriesArea]);

  return (
    <div className={styles['categories-area']}>
      {props.categoriesArea.map((f, index) => {
        return (
          <div className={styles['cat-wrapper']}>
            <div
              id={'cat-' + index}
              className={styles.header}
              tabIndex={0}
              onClick={(e) => {
                if (categoryJustClosed?.id === f.id) setCategoryJustClosed(undefined);
                else toggleFilter(f);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.keyCode === 32 /** Spacebar */) {
                  toggleFilter(f);
                }
              }}>
              {f.name} <Icon width={20} name={f !== openCategory ? 'chev-right' : 'close'} />
            </div>

            {openCategory === f && (
              <div
                className={`${styles.modal} ${openCategory === f ? styles.show : styles.hide}`}
                ref={(el) => (modalRef.current[index] = el)}>
                <div className={styles.header}>{openCategory.name}</div>
                <CustomScrollbar
                  showOnLoad={true}
                  ref={(el: any) => (ps.current[index] = el)}
                  className={styles.list}
                  width={(f.childCategories?.length ?? 0) > 20 ? 'thick' : 'default'}
                  showYButtons={(f.childCategories?.length ?? 0) > 20}>
                  <div className={styles.options}>
                    {f.childCategories?.map((cc) => (
                      <Button
                        href={props.getSRPFamilyPageUrl(cc.id)}
                        buttonType='text'
                        className={styles.family}
                        onClick={() => props.plpService.tagSRPFamilySelection(cc.id)}>
                        {cc.name} ({cc.noOfProducts})
                      </Button>
                    ))}
                  </div>
                </CustomScrollbar>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  function toggleFilter(facet: IPLPCategoryItem | undefined) {
    setOpenCategory((curr) => {
      if (curr?.id === facet?.id) return undefined;
      else return facet;
    });
  }
};
